import type { AppRouterOutputs } from '@alison/api/router';
import type { ScoreThresholds } from './scoreScale.js';

type OverviewOutput = AppRouterOutputs['flightCheck']['overview'];
type BatchPageOutput = AppRouterOutputs['flightCheck']['batches'];
type BatchOutput = BatchPageOutput['batches'][number];

export type FlightCheckCopy = OverviewOutput['flightCheckCopy'];

export interface NetworkRef {
  slug: string | null;
  label: string;
}

export interface NetworkScore {
  network: NetworkRef;
  score: number;
}

export interface BatchCreative {
  id: string;
  name: string;
  score: number;
  thumbnailUrl: string | null;
}

export interface LatestBatch {
  name: string;
  ranAt: string;
  overall: number;
  creativeCount: number;
  needsAttention: number;
  networks: NetworkScore[];
  creatives: BatchCreative[];
}

export interface Kpis {
  creativesAnalyzed: number;
  batchCount: number;
  averageScore: number | null;
  latestBatchScore: number | null;
  strongCount: number;
  needsAttentionCount: number;
}

export interface BreakdownColumn {
  key: string;
  label: string;
  network?: NetworkRef;
  score: number;
  strongPercent: number;
  moderatePercent: number;
  weakPercent: number;
}

export interface TrendPoint {
  id: string;
  ranAt: string;
  score: number;
}

export interface WeakestCategory {
  name: string;
  percent: number;
}

export interface NetworkRow {
  network: NetworkRef;
  score: number;
  bestPractice: number | null;
  performance: number | null;
  brandGuidelines: number | null;
  weakest: WeakestCategory | null;
  lowestCreative: { score: number; thumbnailUrl: string | null };
  type: string | null;
}

export interface BatchSummary {
  id: string;
  name: string;
  ranAt: string;
  creativeCount: number;
  // Set while scoring or after a failure; such a batch has no network rows.
  statusLabel: string | null;
  rows: NetworkRow[];
}

export interface LatestBatchStrip {
  name: string;
  ranAt: string;
  creativeCount: number;
  overall: number;
  bestPractice: number | null;
  performance: number | null;
  brandGuidelines: number | null;
  weakest: WeakestCategory | null;
  networks: NetworkScore[];
  needsAttention: number;
}

export interface DashboardView {
  kpis: Kpis;
  latestBatch: LatestBatch | null;
  breakdown: { platforms: BreakdownColumn[]; types: BreakdownColumn[] };
  trend: { insight: string | null; points: TrendPoint[] };
  thresholds: ScoreThresholds;
}

export interface AnalysisView {
  latest: LatestBatchStrip | null;
  batches: BatchSummary[];
  thresholds: ScoreThresholds;
}

// A row the service left unnamed still needs a heading; its id is the only generic one.
function nameOf(name: string | null, id: string): string {
  return name ?? id;
}

function networkRef({ slug, label }: NetworkRef): NetworkRef {
  return { slug, label };
}

function batchSummary(batch: BatchOutput): BatchSummary {
  return {
    id: batch.rootRunId,
    name: nameOf(batch.name, batch.rootRunId),
    ranAt: batch.listedAt,
    creativeCount: batch.creativeCount,
    statusLabel: batch.status === 'completed' ? null : batch.statusLabel,
    rows: batch.networks.map((row) => ({
      network: networkRef(row.network),
      score: row.score,
      bestPractice: row.bestPractice,
      performance: row.performance,
      brandGuidelines: row.brandGuidelines,
      weakest: row.weakestAttribute,
      lowestCreative: {
        score: row.lowestCreative.score,
        thumbnailUrl: row.lowestCreative.thumbnailUrl,
      },
      type: row.lowestCreative.creativeTypeLabel,
    })),
  };
}

function latestStrip(batch: BatchOutput | null): LatestBatchStrip | null {
  if (batch === null || batch.overall === null) return null;
  return {
    name: nameOf(batch.name, batch.rootRunId),
    ranAt: batch.listedAt,
    creativeCount: batch.creativeCount,
    overall: batch.overall,
    bestPractice: batch.bestPractice,
    performance: batch.performance,
    brandGuidelines: batch.brandGuidelines,
    weakest: batch.weakestAttribute,
    networks: batch.networks.map((row) => ({ network: networkRef(row.network), score: row.score })),
    needsAttention: batch.needsAttention ?? 0,
  };
}

export function toDashboardView(overview: OverviewOutput): DashboardView {
  const latest = overview.latestBatch;
  return {
    kpis: {
      creativesAnalyzed: overview.kpis.creatives,
      batchCount: overview.kpis.batches,
      averageScore: overview.kpis.average,
      latestBatchScore: overview.kpis.latestBatchAverage,
      strongCount: overview.kpis.strong,
      needsAttentionCount: overview.kpis.needsAttention,
    },
    latestBatch:
      latest === null
        ? null
        : {
            name: nameOf(latest.name, latest.rootRunId),
            ranAt: latest.completedAt,
            overall: latest.overall,
            creativeCount: latest.creativeCount,
            needsAttention: latest.needsAttention,
            networks: latest.networks.map((row) => ({
              network: networkRef(row.network),
              score: row.score,
            })),
            creatives: latest.creatives.map((creative) => ({
              id: creative.creativeId,
              name: nameOf(creative.name, creative.creativeId),
              score: creative.overall,
              thumbnailUrl: creative.thumbnailUrl,
            })),
          },
    breakdown: {
      platforms: overview.byNetwork.map((row) => ({
        key: String(row.network.networkId),
        label: row.network.label,
        network: networkRef(row.network),
        score: row.average,
        strongPercent: row.shares.strong,
        moderatePercent: row.shares.moderate,
        weakPercent: row.shares.weak,
      })),
      types: overview.byType.map((row) => ({
        key: row.creativeType,
        label: row.label,
        score: row.average,
        strongPercent: row.shares.strong,
        moderatePercent: row.shares.moderate,
        weakPercent: row.shares.weak,
      })),
    },
    trend: {
      insight: overview.insight,
      points: overview.trend.map((point) => ({
        id: point.rootRunId,
        ranAt: point.completedAt,
        score: point.overall,
      })),
    },
    thresholds: overview.thresholds,
  };
}

export function toAnalysisView(page: BatchPageOutput): AnalysisView {
  return {
    latest: latestStrip(page.latestBatch),
    batches: page.batches.map(batchSummary),
    thresholds: page.thresholds,
  };
}

export function fillQuery(template: string, query: string): string {
  return template.replaceAll('{query}', query);
}
